import PropTypes from "prop-types";
import { useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogActions } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CheckIcon, DeleteIcon } from "./viewer-icons";

// same 56px round-button look as the model viewer's control panel
const roundButtonSx = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  p: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Fullscreen preview of a captured image, with the same discard/save control
// panel as the model viewer.
// deleteConfirm: ask before discarding — used when reviewing an already-saved
// image; a fresh (unsaved) photo is just retaken without a dialog.
export const ImagePreview = ({
  src,
  onDelete,
  onSave,
  deleteConfirm = false,
}) => {
  const theme = useTheme();
  const red = theme.palette.error.main;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        backgroundColor: "#000",
      }}
    >
      <Box
        component="img"
        src={src}
        alt="Captured image"
        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
      />

      {/* translucent control panel with discard/save buttons */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          px: 3,
          pt: 2.5,
          pb: "max(20px, env(safe-area-inset-bottom))",
          // more opaque than the model viewer's panel
          backgroundColor: "rgba(200, 200, 200, 0.75)",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
        }}
      >
        <Box sx={{ justifySelf: "start" }}>
          <Box
            component="button"
            type="button"
            aria-label="Delete"
            onClick={() => (deleteConfirm ? setConfirmOpen(true) : onDelete())}
            sx={{
              ...roundButtonSx,
              backgroundColor: "transparent",
              border: `2px solid ${red}`,
            }}
          >
            <DeleteIcon size={32} color={red} />
          </Box>
        </Box>
        <Box sx={{ justifySelf: "center" }}>
          <Box
            component="button"
            type="button"
            aria-label="Save"
            onClick={onSave}
            sx={{
              ...roundButtonSx,
              backgroundColor: "primary.main",
              border: "2px solid #fff",
            }}
          >
            <CheckIcon size={32} color="#fff" />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>Delete this image and retake?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              setConfirmOpen(false);
              onDelete();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ImagePreview.propTypes = {
  src: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  deleteConfirm: PropTypes.bool,
};
